import React, { useEffect, useState } from 'react';
import {useParams} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {CollapsibleSection} from '~/components/CollapsibleSection';
import {DetailsView} from '~/components/DetailsView';
import detailsStyle from '~/components/DetailsView/index.module.scss';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {JsonViewerModal} from '~/components/JsonViewerModal';
import {BreadcrumbsResourcePage, ResourceBlock} from '~/components/ResourcePage';
import {useAbility} from '~/lib/ability';
import {renderBoolean} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {formatJsonValue} from '~/lib/json';
import {ResourceLoader, useResource} from '~/lib/loader';
import {clientsLinks} from '~/modules/Clients';
import {ClientsAbility} from '~/modules/Clients/ability';
import {
    fetchPaymentDetails,
    fetchSeller,
    fetchSellerAccountInfo,
    updatePaymentDetails,
    updateSeller
} from '~/modules/Clients/api';
import {PaymentDetailsForm} from '~/modules/Clients/forms/PaymentDetailsForm';
import {SellerForm} from '~/modules/Clients/forms/SellerForm';
import {configurationToDetailsView} from '~/modules/Clients/mappers';
import {Configuration, configurationPresent} from '~/modules/Clients/models/Configuration';
import {TakeRateCapType, takeRateCapTypeLabels} from '~/modules/Clients/models/AccountInfo';
import {paymentsLinks} from '~/modules/Payments/links';
import {SellerBlockings} from "~/modules/Clients/components/SellerBlockings";
import {SellerSensitiveCategories} from "~/modules/Clients/components/SellerSensitiveCategories";
import {SellerSources} from "~/modules/Clients/components/SellerSources";
import {SellerFinance} from "~/modules/Clients/components/SellerFinance";
import { fetchFinanceTransaction, IFinancePayment } from '~/modules/Payments/api';
import { InvoicePaymentsModal } from '~/modules/Payments/components/InvoicePaymentsModal';
import {useAccountManagersQuery, useSalesRepresentativesQuery} from '~/modules/Dictionary/queries';
import {SecretText} from '~/components/SecretText';

interface ISellerViewParams {
    sellerId: string;
}

const getConfigurationFields = (configuration: Configuration) => {
    if (!configurationPresent(configuration)) {
        return null;
    }
    if (typeof configuration !== 'string') {
        return null;
    }
    try {
        return configurationToDetailsView(configuration).sort((a, b) => a.label.localeCompare(b.label));
    } catch {
        return [];
    }
};

export const SellerView = () => {
    const params = useParams<ISellerViewParams>();
    const sellerResult = useResource(() => fetchSeller(params.sellerId));
    const paymentDetailsResult = useResource(() => fetchPaymentDetails(params.sellerId));
    const accountManagersQuery = useAccountManagersQuery();
    const salesRepresentativesQuery = useSalesRepresentativesQuery();

    const accountManagersResult = {
        isPending: accountManagersQuery.isPending,
        resource: accountManagersQuery.data ?? [],
        setResource: (_resource: NonNullable<typeof accountManagersQuery.data>) => undefined,
        fetch: () => {
            void accountManagersQuery.refetch();
        },
    };

    const salesRepresentativesResult = {
        isPending: salesRepresentativesQuery.isPending,
        resource: salesRepresentativesQuery.data ?? [],
        setResource: (_resource: NonNullable<typeof salesRepresentativesQuery.data>) => undefined,
        fetch: () => {
            void salesRepresentativesQuery.refetch();
        },
    };
    const ability = useAbility(ClientsAbility);
    const modal = useModals();
    const invoicesModal = modal.create(InvoicePaymentsModal, {});
    const jsonViewerModal = modal.create(JsonViewerModal);

    const [invoicePayments, setInvoicePayments] = useState<IFinancePayment[]>([]);
    const [accountIsActive, setAccountIsActive] = useState<boolean>(true);

    useEffect(() => {
        if (!params.sellerId) return;
        fetchFinanceTransaction(params.sellerId)
            .then((data) => setInvoicePayments(data?.payments ?? []))
            .catch(() => setInvoicePayments([]));

        fetchSellerAccountInfo(params.sellerId)
            .then((accountInfo) => setAccountIsActive(accountInfo.isActive))
            .catch(() => setAccountIsActive(false));
    }, [params.sellerId]);

    const editSeller = modal.create(SellerForm, {
        onSubmit: (seller, formikActions, close) => {
            updateSeller(seller)
                .then(sellerResult.setResource).then(close).catch(onSubmitError(formikActions));
        }
    });

    const paymentDetailsEdit = modal.create(PaymentDetailsForm, {
        onSubmit: (paymentDetails, formikActions, close) => {
            updatePaymentDetails({
                ...paymentDetails,
                sellerId: params.sellerId
            }).then(paymentDetailsResult.setResource).then(close)
                .catch(onSubmitError(formikActions, response => formikActions.setErrors(response.data)));
        }
    });

    return <ResourceLoader result={sellerResult} message='Loading seller'>
        {({resource: seller, isPending}) => {
            const configurationFields = getConfigurationFields(seller.configuration);
            const configurationText = configurationFields ? formatJsonValue(seller.configuration) : '';
            return (
                <ResourceLoader result={accountManagersResult} message='Loading account managers'>
                    {({resource: accountManagers}) =>
                        <ResourceLoader result={salesRepresentativesResult} message='Loading sales representatives'>
                            {({resource: salesRepresentatives}) =>
                            <Breadcrumb breadcrumb={{to: clientsLinks.sellers(), title: 'Sellers'}}>
                                <Breadcrumb breadcrumb={{to: clientsLinks.agency(seller.id), title: seller.name}}>
                                    <BreadcrumbsResourcePage title='Seller' toolbar={<>
                                        <Button
                                            responsive
                                            leftIcon={<i className='fa fa-dollar'/>}
                                            to={paymentsLinks.payments(seller.id)}>
                                            Payments
                                        </Button>
                                        {invoicePayments.length > 0 && accountIsActive && (
                                            <Button
                                                responsive
                                                intent={Intent.SUCCESS}
                                                leftIcon={<i className='far fa-file-pdf'/>}
                                                onClick={() => invoicesModal.show({
                                                    sellerId: Number(params.sellerId),
                                                    payments: invoicePayments
                                                })}
                                            >
                                                Invoices
                                            </Button>
                                        )}
                                        <Button responsive
                                                key='payment-details'
                                                disabled={isPending}
                                                intent={Intent.WARNING}
                                                leftIcon={<i className='fa fa-credit-card'/>}
                                                onClick={() => paymentDetailsEdit.show({paymentDetails: paymentDetailsResult.resource})}>
                                            Payment Details
                                        </Button>
                                        {ability.canEditSeller(seller) && <Button responsive
                                                                                  key='edit'
                                                                                  disabled={isPending}
                                                                                  leftIcon={<i className='fa fa-edit'/>}
                                                                                  onClick={() => editSeller.show({seller})}>
                                            Edit
                                        </Button>}
                                    </>}>
                                        <DetailsView
                                            fields={[
                                                {label: 'Name', value: seller.name},
                                                {label: 'Active', value: renderBoolean(seller.active)},
                                                {label: 'Conditions', value: seller.conditions},
                                                {label: 'Payment Type', value: seller.paymentType},
                                                {
                                                    label: 'Take Rate Cap Type',
                                                    value: takeRateCapTypeLabels[seller.takeRateCap?.type] ?? takeRateCapTypeLabels[TakeRateCapType.DISABLED]
                                                },
                                                ...(seller.takeRateCap?.type && seller.takeRateCap.type !== TakeRateCapType.DISABLED
                                                    ? [{label: 'Take Rate Cap', value: seller.takeRateCap.value != null ? `${seller.takeRateCap.value}%` : 'N/A'}]
                                                    : []),
                                                {
                                                    label: 'Account Manager',
                                                    value: accountManagers.find(m => m.id === seller.accountManagerId)?.name || 'N/A'
                                                },
                                                {
                                                    label: 'Sales Representative',
                                                    value: salesRepresentatives.find(m => m.id === seller.salesRepresentativeId)?.name || 'N/A'
                                                }
                                            ]}/>
                                        <DetailsView
                                            title='SSP'
                                            fields={[
                                                {label: 'Reporting API URL', value: seller.attributes?.reportingApiUrl},
                                                {label: 'Access Token', value: <SecretText value={seller.attributes?.accessToken}/>},
                                                {label: 'Access Username', value: seller.attributes?.accessUsername},
                                                {label: 'Access Password', value: <SecretText value={seller.attributes?.accessPassword}/>},
                                                {label: 'Publisher ID', value: seller.attributes?.publisherId},
                                            ]}/>
                                        {configurationFields && (
                                            <ResourceBlock title='Configuration'>
                                                <CollapsibleSection
                                                    collapsedHeight={220}
                                                    canCopy
                                                    copyText={configurationText}
                                                    footerActions={
                                                        <Button
                                                            small
                                                            onClick={() =>
                                                                jsonViewerModal.show(
                                                                    {title: 'Configuration', value: seller.configuration},
                                                                    {size: 'wide'}
                                                                )
                                                            }
                                                        >
                                                            View JSON
                                                        </Button>
                                                    }
                                                >
                                                    {configurationFields.length ? (
                                                        <div className={detailsStyle.detailsView}>
                                                            {configurationFields.map(field => (
                                                                <React.Fragment key={field.label}>
                                                                    <div className={detailsStyle.detailsLabel}>
                                                                        {field.label}:
                                                                    </div>
                                                                    <div className={detailsStyle.detailsValue}>
                                                                        {field.value}
                                                                    </div>
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div>Invalid configuration</div>
                                                    )}
                                                </CollapsibleSection>
                                            </ResourceBlock>
                                        )}
                                        <div className="layout-content-box">
                                            <SellerBlockings sellerId={params.sellerId}/>
                                        </div>
                                        <div className="layout-content-box">
                                            <SellerSensitiveCategories sellerId={params.sellerId}/>
                                        </div>
                                        <div className="layout-content-box">
                                            <SellerSources sellerId={params.sellerId} viewOnly={false}/>
                                        </div>
                                        <div className="layout-content-box">
                                            <SellerFinance sellerId={params.sellerId} sellerName={seller.name} viewOnly={false}/>
                                        </div>
                                    </BreadcrumbsResourcePage>
                                </Breadcrumb>
                            </Breadcrumb>}
                        </ResourceLoader>}
                </ResourceLoader>
            );
        }}
    </ResourceLoader>;
};
